import type { ModelsPublicSportType } from '@/api/generated-api'
import type { PublicSportType } from '@/features/sport-types/api/sportTypesApi'
import { findSportType } from '@/features/sport-types/lib/findSportType'
import { getSportTypeIconUrl } from '@/features/sport-types/lib/sportTypeIcon'

type ClubSport = ModelsPublicSportType | PublicSportType

export type ClubSportReference = {
  sport?: ClubSport | null
  sport_type?: string | null
}

export type ClubSportDisplay = {
  name: string
  iconUrl?: string
}

function sportRefsAlign(enriched: ClubSport, sportTypeRef: string): boolean {
  const ref = sportTypeRef.trim()
  if (enriched.id === ref) {
    return true
  }
  return enriched.code?.toUpperCase() === ref.toUpperCase()
}

function displayFromSportType(
  sportTypes: PublicSportType[],
  sportTypeRef: string | null | undefined
): ClubSportDisplay | null {
  const fromDictionary = findSportType(sportTypes, sportTypeRef)
  if (!fromDictionary) {
    return null
  }
  return {
    name: fromDictionary.name,
    iconUrl: getSportTypeIconUrl(fromDictionary)
  }
}

function formatSportTypeCode(code: string): ClubSportDisplay {
  const name = code
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
  return { name }
}

function looksLikeSportCode(ref: string): boolean {
  return /^[A-Z][A-Z0-9_]*$/.test(ref.trim())
}

export function resolveClubSportDisplay(
  club: ClubSportReference,
  sportTypes: PublicSportType[] = []
): ClubSportDisplay | null {
  const sportTypeRef = club.sport_type?.trim()
  const enriched = club.sport
  const enrichedConflict =
    Boolean(sportTypeRef) &&
    Boolean(enriched?.name?.trim()) &&
    !sportRefsAlign(enriched!, sportTypeRef!)

  // sport_type is the column leaderboards and SetClubSportType keep in sync;
  // sport enrichment follows sport_type_id and can lag after admin reassignment.
  if (sportTypeRef && enrichedConflict) {
    const canonical = displayFromSportType(sportTypes, sportTypeRef)
    if (canonical) {
      return canonical
    }
    if (looksLikeSportCode(sportTypeRef)) {
      return formatSportTypeCode(sportTypeRef)
    }
    return null
  }

  if (sportTypeRef) {
    const canonical = displayFromSportType(sportTypes, sportTypeRef)
    if (canonical) {
      return canonical
    }
  }

  if (enriched?.name?.trim()) {
    return {
      name: enriched.name.trim(),
      iconUrl: getSportTypeIconUrl(enriched)
    }
  }

  // The public dictionary lists ACTIVE sports only, and sports are created or
  // switched off at any time: a known code still gets a readable name.
  if (sportTypeRef && looksLikeSportCode(sportTypeRef)) {
    return formatSportTypeCode(sportTypeRef)
  }
  return null
}

export function clubSportName(
  club: ClubSportReference,
  sportTypes: PublicSportType[] = []
): string {
  return resolveClubSportDisplay(club, sportTypes)?.name ?? '—'
}
